import React, { useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
} from "@mui/material";
import {
  MuiInput,
  MuiButton,
  TourRide,
  TourStep,
} from "../../../../components";
import { useForm, Form } from "../../../../components/mui/useForm";
import { useNavigate } from "react-router-dom";
import Notification from "../../../../components/mui/Notification";
import LoadingButton from "@mui/lab/LoadingButton";
import PageHeader from "../../../../components/PageHeader";
import * as institutionService from "../../services/InstitutionService";
import AutoCompleteAsync from "../../../../components/AutoCompleteAsync";
import { isEmailDomainValid } from "./functions";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const initialFValues = {
  email: "",
  institutionId: "",
};

export default function New() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);
  const [domain, setDomain] = useState();
  const [openNotFoundDialog, setOpenNotFoundDialog] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState();
  const [requestError, setRequestError] = useState();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCloseNotFoundDialog = () => {
    setOpenNotFoundDialog(false);
  };

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ("email" in fieldValues) {
      temp.email =
        fieldValues.email !== "" && /$^|.+@.+..+/.test(fieldValues.email)
          ? isEmailDomainValid(fieldValues.email, domain)
            ? ""
            : domain
            ? t("institution_new_email_address_domain_error") + domain
            : t("institution_new_email_address_institution_error")
          : t("form_error_invalid_email");
    }
    if ("institutionId" in fieldValues) {
      temp.institution = fieldValues.institutionId
        ? ""
        : t("form_error_field_required");
    }
    setErrors({
      ...temp,
    });

    if (fieldValues === values)
      return Object.values(temp).every((x) => x === "");
  };

  const { values, setValues, errors, setErrors, handleInputChange, resetForm } =
    useForm(initialFValues, true, validate);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      institutionService
        .create(values)
        .then((response) => {
          showNotificationAndGoBack();
        })
        .catch((error) => {
          console.error(`Error: ${error.message}`);
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (error.response.status === 409) {
              setNotify({
                isOpen: true,
                message: t("institution_new_server_error_409"),
                type: "error",
              });
            } else {
              setNotify({
                isOpen: true,
                message: t("institution_new_server_error_default"),
                type: "error",
              });
            }
          } else {
            setNotify({
              isOpen: true,
              message: t("server_error_default"),
              type: "error",
            });
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const showNotificationAndGoBack = () => {
    setNotify({
      isOpen: true,
      message: t("form_submission_success"),
      type: "success",
    });
    setTimeout(() => {
      // Add your logic for the transition
      navigate(-1, { replace: true });
    }, 1000);
  };

  const onInstitutionChange = (event, institution) => {
    setValues({ ...values, institutionId: institution.id });
    validate({ institutionId: institution.id });
    setDomain(institution.domain);
  };

  const tourSteps = [
    {
      target: "#auto-complete-asynchronous",
      content: (
        <TourStep
          title={"Institution Name"}
          description={
            "Find your institution by start typing. If not found click on ADD NEW INSTITUTION"
          }
        />
      ),
    },
    {
      target: "#asyncautocomplete-notfound-icon",
      content: (
        <TourStep
          title={"Add your institution"}
          description={
            "If your institution is not listed add it by clikcing on this icon."
          }
        />
      ),
    },
    {
      target: "#institution-new-page-email",
      content: (
        <TourStep
          title={"Institution email address"}
          description={
            "Enter your institution email address, and wait for confirmation email."
          }
        />
      ),
    },
  ];

  return (
    <>
      <TourRide name="institution-new-page" steps={tourSteps} />
      <Form onSubmit={handleSubmit} fullWidth>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <PageHeader title={t("institution_new_title")} isChild={true} />
          <Paper variant="outlined" sx={{ my: { xs: 3 }, p: { xs: 2, md: 3 } }}>
            <React.Fragment>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={7}>
                  <AutoCompleteAsync
                    noOptionsText={
                      <MuiButton
                        size="small"
                        text={t("institution_not_found")}
                        color="info"
                        variant="text"
                        onClick={() => {
                          setOpenNotFoundDialog(true);
                        }}
                      />
                    }
                    name="institution"
                    error={errors.institution}
                    label={t("institution")}
                    getOptions={() => institutionService.getAll()}
                    onChange={onInstitutionChange}
                    handleNotFound={() => setOpenNotFoundDialog(true)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MuiInput
                    id="institution-new-page-email"
                    name="email"
                    label={t("institution_new_email_address")}
                    value={values.email}
                    onChange={handleInputChange}
                    error={errors.email}
                  />
                </Grid>
              </Grid>
            </React.Fragment>
          </Paper>

          <Grid item xs={4} sm={8} md={8} align="center">
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction={{ xs: "column", sm: "row" }}
              sx={{ ml: 1 }}
            >
              <LoadingButton
                type="submit"
                color="success"
                variant="outlined"
                loading={loading}
              >
                {t("form_submit")}
              </LoadingButton>

              <MuiButton
                text={t("form_reset")}
                color="inherit"
                variant="outlined"
                onClick={resetForm}
              />

              <MuiButton
                text={t("form_cancel")}
                color="info"
                variant="outlined"
                onClick={() => {
                  navigate(-1);
                }}
              />
            </Stack>
          </Grid>
          <Notification notify={notify} setNotify={setNotify} />
        </Container>
      </Form>
      <Dialog
        fullScreen={fullScreen}
        open={openNotFoundDialog}
        onClose={handleCloseNotFoundDialog}
        PaperProps={{
          component: "form",
          onSubmit: (event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries(formData.entries());
            const institution = formJson.institution?.trim();
            if (!institution) {
              setRequestError(t("institution_new_dialog_institution_error"));
              return;
            }
            setLoadingRequest(true);
            institutionService
              .request(institution)
              .then((res) =>
                setNotify({
                  isOpen: true,
                  message: t("institution_new_dialog_success_msg"),
                  type: "success",
                })
              )
              .catch((e) => {
                console.error(`Error: ${e.message}`);
              })
              .finally(() => {
                setLoadingRequest(false);
                handleCloseNotFoundDialog();
              });
          },
        }}
      >
        <DialogTitle>{t("institution_new_dialog_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("institution_new_dialog_content")}
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            id="institution"
            name="institution"
            label={t("institution_new_dialog_institution")}
            fullWidth
            variant="standard"
            error={Boolean(requestError)}
            helperText={requestError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotFoundDialog}>
            {t("form_cancel")}
          </Button>
          <LoadingButton loading={loadingRequest} type="submit">
            {t("form_submit")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
